// One audio element, reused for the life of the backend, that streams a track
// by range requests through the shared AudioContext so playback starts before
// the whole file has arrived. The element is wrapped in a media element source
// once, because wrapping the same element twice throws. Output goes through a
// gain node that stays at zero until the element is actually playing at the
// requested position, so nothing from before the seek is heard.
const OPEN_SECONDS = 0.02;

export class ElementStream {
  constructor(context) {
    this.ctx = context;
    this.element = new Audio();
    this.element.crossOrigin = "anonymous";
    this.element.preload = "auto";
    this.gain = context.createGain();
    this.gain.gain.value = 0;
    context.createMediaElementSource(this.element).connect(this.gain);
    this.gain.connect(context.destination);
    this.url = null;
    this.pendingSeek = null;
    this.active = false;
    this.opened = false;
    this.fadeTimer = null;
    this.onPlaying = () => {};
    this.onWaiting = () => {};
    this.onTimeUpdate = () => {};
    this.onEnded = () => {};
    this.onError = () => {};
    this.listen();
  }

  listen() {
    const el = this.element;
    el.addEventListener("loadedmetadata", () => {
      if (this.pendingSeek === null) return;
      el.currentTime = this.pendingSeek;
      this.pendingSeek = null;
    });
    el.addEventListener("playing", () => this.open());
    el.addEventListener("seeked", () => this.open());
    el.addEventListener("waiting", () => {
      if (this.active) this.onWaiting();
    });
    el.addEventListener("timeupdate", () => {
      if (this.active) this.onTimeUpdate(el.currentTime);
    });
    el.addEventListener("ended", () => {
      if (this.active) this.onEnded();
    });
    el.addEventListener("error", () => {
      if (this.active) this.onError(new Error(`Failed to stream ${this.url}`));
    });
  }

  // Must be called synchronously inside the user gesture that starts playback.
  start(url, position) {
    this.clearFadeTimer();
    this.active = true;
    this.opened = false;
    this.mute();
    if (url !== this.url) {
      this.url = url;
      this.element.src = url;
      this.pendingSeek = position;
    } else if (this.element.readyState >= 1) {
      this.pendingSeek = null;
      this.element.currentTime = position;
    } else {
      this.pendingSeek = position;
    }
    this.element.play().catch((error) => {
      if (this.active) this.onError(error);
    });
  }

  open() {
    const el = this.element;
    if (!this.active || this.opened || el.paused || el.seeking || this.pendingSeek !== null) return;
    this.opened = true;
    const now = this.ctx.currentTime;
    this.gain.gain.cancelScheduledValues(now);
    this.gain.gain.setValueAtTime(0, now);
    this.gain.gain.linearRampToValueAtTime(1, now + OPEN_SECONDS);
    this.onPlaying();
  }

  mute() {
    const now = this.ctx.currentTime;
    this.gain.gain.cancelScheduledValues(now);
    this.gain.gain.setValueAtTime(0, now);
  }

  position() {
    return this.element.currentTime;
  }

  pause() {
    this.clearFadeTimer();
    this.active = false;
    this.mute();
    this.element.pause();
  }

  // Stops the element and drops its src so a paused element with preload
  // auto cannot keep filling its buffer and competing for bandwidth with the
  // decode fetch. A later start() with the same url reloads from scratch
  // rather than seeking in place, since the src is gone.
  release() {
    this.clearFadeTimer();
    this.active = false;
    this.mute();
    this.element.pause();
    this.element.removeAttribute("src");
    this.element.load();
    this.url = null;
  }

  clearFadeTimer() {
    if (this.fadeTimer === null) return;
    clearTimeout(this.fadeTimer);
    this.fadeTimer = null;
  }

  // Ramps to silence on the context clock while the decoded buffer ramps in,
  // then releases the element once the ramp is over unless it was restarted.
  // If the gain never opened there is nothing audible to fade, so release now.
  fadeOut(at, duration) {
    this.active = false;
    if (!this.opened) {
      this.release();
      return;
    }
    // An explicit anchor at `at` is required: a ramp with no preceding event
    // at or after the handoff moment would start from the last event in the
    // past and fade the element out before the buffer has ramped in.
    const gain = this.gain.gain;
    gain.cancelScheduledValues(at);
    gain.setValueAtTime(1, at);
    gain.linearRampToValueAtTime(0, at + duration);
    const wait = Math.max(0, at + duration - this.ctx.currentTime) * 1000 + 20;
    this.fadeTimer = setTimeout(() => {
      this.fadeTimer = null;
      this.release();
    }, wait);
  }

  destroy() {
    this.release();
    this.gain.disconnect();
  }
}
